import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Account = components['schemas']['AccountResponse'];

export const accountQueryKey = ['account'] as const;

/**
 * The business itself — its timezone and its business hours.
 *
 * Written for the dispatch board (M1-09) and moved here at M1-04, when the
 * customer's service history needed the same answer to the same question:
 * which clock is a date on? Both screens print times the business keeps, not
 * times the viewer's laptop keeps, and a second copy of this query would be a
 * second cache entry for one fact.
 *
 * Long `staleTime`: opening hours change when an owner edits them, not while
 * a dispatcher works. Refetching this alongside every board load would be one
 * request per date change for data that is effectively static.
 */
export function useAccount() {
  return useQuery({
    queryKey: accountQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/account');
      // `!response.ok || !data` rather than `if (error)` alone. openapi-fetch
      // only populates `error` when the failure body parses as the declared
      // problem+json; a gateway 502 is an HTML page, and an `error`-only guard
      // falls through it and returns `undefined`. React Query does catch that
      // and fails the query, so the screen still reaches its error state — but
      // via a console warning about a bug in this function rather than via the
      // status code, and nothing tells you which request failed. Checking the
      // response says so directly.
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/account failed: ${response.status}`);
      }
      return data;
    },
    staleTime: 10 * 60_000,
  });
}
